/*
 * Copyright 2022, Corning Optical Communications
 * All Rights Reserved.
 */

import React, { useEffect, useState } from "react";
import { NODEROUTES, ROUTES } from "../../globals/globals";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Button, Dialog, DialogContent, DialogTitle } from "@mui/material";
import NodeLeftSideNavigation from "../../components/Nodes/NodeLeftSideNavigation";
import { useDispatch, useSelector } from "react-redux";
import { accessControlSettingsSelector, showBannerSelector } from "../../store/api/admin/selectors";
import { setShowBanner } from "../../store/api/admin/reducers";
import { LoginBanner } from "../../components/Administrations/AccessControls/Users/Banner/Banner";
import { usernameSelector } from "../../store/api/auth/selectors";
import { useGetUserBySelfQuery, useClearFailedLoginMutation } from "../../store/api/admin/adminApiSlice";
import { GetPasswordExpiryData } from "../../components/Administrations/helpers";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import { useStoreDispatch } from "../../store/hooks";
import { resetKeyword } from "../../store/search/reducer";
import { setActiveTab, setDataGridSort } from "../../store/helpers/helperReducers";
import { setNoOfSelectedRow } from "../../store/api/devices/reducers";
import { resetEventFilters } from "../../store/filters/events/EventFilterReducer";
import { resetEventFilters as resetAlarmFilters } from "../../store/filters/alarms/reducers";
import {
  SidebarProvider,
  Sidebar,
  SidebarHeader,
  SidebarContent,
  SidebarInset,
  SidebarTrigger,
  SidebarRail,
} from "../../components/ui/sidebar";
import { Separator } from "../../components/ui/separator";
import { Tabs, TabsList, TabsTrigger } from "../../components/ui/tabs";

const Nodes: React.FC = () => {
  const dispatch = useDispatch();
  const storeDispatch = useStoreDispatch();
  const location = useLocation();
  const navigate = useNavigate();
  const routes = Object.values(NODEROUTES);

  const [localBannerState, setLocalBannerState] = useState(false);
  const [clearFailedLogin] = useClearFailedLoginMutation();

  const username = useSelector(usernameSelector);
  const { data: user } = useGetUserBySelfQuery({ name: username });
  const showBanner = useSelector(showBannerSelector);
  const accessControlSettings = useSelector(accessControlSettingsSelector);
  const { showPasswordExpiryMessage } = GetPasswordExpiryData(user, accessControlSettings);
   const hasFailedLogins = (user?.failedLoginCount ?? 0) > 0 && user?.failedLoginDateTime != null;

  useEffect(() => {
    if ((accessControlSettings?.bannerEnabled || showPasswordExpiryMessage || hasFailedLogins) && showBanner) {
      setLocalBannerState(true);
      dispatch(setShowBanner(false));
    }
  }, [accessControlSettings, user]);

  const currentRoute =
    routes.find((route) => {
      return location.pathname === `/${ROUTES.NODES.Path}/${route.Path}`;
    }) || NODEROUTES.NODESPANEL;

  useEffect(() => {
    const previousRoute = localStorage.getItem("currentRoute");
    if (previousRoute) {
      localStorage.setItem("previousRoute", previousRoute);
    }
    localStorage.setItem("currentRoute", location.pathname);
  }, [location]);

  return (
    <>
      {/* ─── Banner Dialog ─── */}
      <Dialog open={localBannerState} fullWidth maxWidth="sm">
        <DialogTitle sx={{ backgroundColor: "var(--card)" }}>
          {accessControlSettings?.bannerEnabled 
          ? accessControlSettings?.bannerTitle : (showPasswordExpiryMessage ? "Password Expiry" : "Security Notice")}
        </DialogTitle>
        <DialogContent sx={{ backgroundColor: "var(--card)" }}>
          <LoginBanner />
          <Button
            variant="contained"
            size="medium"
            color="secondary"
            sx={{ float: "right" }}
            onClick={() => {
              setLocalBannerState(false);
              if (hasFailedLogins) clearFailedLogin();
            }}
          >
            OK
          </Button>
        </DialogContent>
      </Dialog>

      {/* ─── Page Layout ─── */}
      <SidebarProvider
        defaultOpen={true}
        data-sidebar-page-bg
        className="h-[calc(100vh-56px)] bg-sidebar overflow-hidden"
      >
        {/* ─── Left Sidebar ─── */}
        <Sidebar
          variant="inset"
          collapsible="offcanvas"
          className="border-0 ml-2"
        >
          <SidebarHeader className="border-b border-sidebar-border h-14 justify-center my-3">
            <div className="px-2 flex items-center gap-2">
              <AccountTreeIcon
                className="text-sidebar-foreground"
                sx={{ fontSize: 18 }}
              />
              <span className="text-sm font-bold text-sidebar-foreground whitespace-nowrap">
                Network Topology
              </span>
            </div>
          </SidebarHeader>
          <SidebarContent>
            <NodeLeftSideNavigation />
          </SidebarContent>
          <SidebarRail />
        </Sidebar>

        {/* ─── Main Content (Inset Card) ─── */}
        <SidebarInset className="bg-sidebar overflow-hidden py-2 pr-2">
          <div className="bg-card h-full rounded-[10px] flex flex-col overflow-hidden">
            {/* ─── Header: Trigger + Separator + Tabs ─── */}
            <Tabs
              value={String(currentRoute.Key)}
              onValueChange={(key) => {
                // Reset filters on tab change (matches old BasicTabs behavior)
                storeDispatch(resetKeyword());
                storeDispatch(setNoOfSelectedRow({ deviceRowIds: [] }));
                storeDispatch(setDataGridSort({}));
                storeDispatch(resetEventFilters());
                storeDispatch(resetAlarmFilters());

                const route = routes.find((r) => String(r.Key) === key);
                if (route) navigate(route.Path, { replace: true });
              }}
            >
              <header className="flex h-14 shrink-0 items-center gap-2 border-b border-border px-4">
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <SidebarTrigger className="-ml-1 text-foreground" />
                  <Separator
                    orientation="vertical"
                    className="mr-2 h-4 shrink-0"
                  />
                  <TabsList className="inline-flex h-auto gap-0 rounded-none border-0 bg-transparent p-0 text-muted-foreground shrink-0">
                    {routes.map((r) => (
                      <TabsTrigger
                        key={r.Key}
                        value={String(r.Key)}
                        className="rounded-none border-b-2 border-transparent bg-transparent px-4 py-2 text-sm capitalize text-muted-foreground data-[state=active]:border-primary data-[state=active]:text-foreground data-[state=active]:bg-transparent data-[state=active]:shadow-none"
                      >
                        {r.Text}
                      </TabsTrigger>
                    ))}
                  </TabsList>
                </div>
              </header>
            </Tabs>

            {/* ─── Route Content ─── */}
            <div className="flex-1 min-h-0 overflow-y-auto px-4 py-4">
              <Outlet />
            </div>
          </div>
        </SidebarInset>
      </SidebarProvider>
    </>
  );
};

export default Nodes;
